import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { theme } from '../../theme';

export const Nav = styled.nav<{ $scrolled: boolean }>`
  position: fixed;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  width: min(calc(100% - 32px), 1180px);
  min-height: 82px;
  padding: 0.75rem 1rem 0.75rem 1.1rem;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: 0.9rem;
  z-index: 1000;
  background: rgba(11, 17, 23, 0.78);
  backdrop-filter: blur(18px);
  border: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(5, 10, 16, 0.18);
  transition: ${theme.transitions.default};

  ${({ $scrolled }) =>
    $scrolled &&
    css`
      background: rgba(11, 17, 23, 0.92);
      border-color: rgba(148, 163, 184, 0.18);
      box-shadow: 0 24px 68px rgba(5, 10, 16, 0.26);
    `}

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: auto auto;
    padding: 0.65rem 0.75rem 0.65rem 0.85rem;
    min-height: 74px;
  }
`;

export const Logo = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: ${theme.colors.text};
`;

export const LogoMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary};
`;

export const LogoText = styled.span`
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  line-height: 1;
`;

export const NavMenu = styled.ul`
  justify-self: center;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem;
  border-radius: 20px;
  background: rgba(8, 12, 18, 0.76);
  border: 1px solid rgba(148, 163, 184, 0.12);

  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

export const NavItem = styled.li``;

export const NavLink = styled(Link)<{ $active?: boolean }>`
  min-width: 92px;
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
  padding: 0.72rem 0.9rem;
  border-radius: 16px;
  border: 1px solid ${({ $active }) => ($active ? `rgba(${theme.colors.primaryRgb}, 0.22)` : 'transparent')};
  background: ${({ $active }) => ($active ? `rgba(${theme.colors.primaryRgb}, 0.12)` : 'transparent')};
  transition: ${theme.transitions.default};

  small {
    font-family: 'Space Grotesk', 'Manrope', sans-serif;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${({ $active }) => ($active ? theme.colors.primaryLight : theme.colors.textMuted)};
  }

  span {
    font-family: 'Space Grotesk', 'Manrope', sans-serif;
    font-size: 0.86rem;
    font-weight: 600;
    color: ${({ $active }) => ($active ? theme.colors.text : theme.colors.textSecondary)};
    line-height: 1.1;
  }

  &:hover {
    transform: translateY(-1px);
    background: rgba(148, 163, 184, 0.08);

    small {
      color: ${theme.colors.primaryLight};
    }

    span {
      color: ${theme.colors.text};
    }
  }
`;

export const NavActions = styled.div`
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 0.7rem;

  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

export const NavCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.78rem 1.05rem;
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${theme.colors.text};
  white-space: nowrap;
  border-radius: 999px;
  background: rgba(${theme.colors.primaryRgb}, 0.12);
  border: 1px solid rgba(${theme.colors.primaryRgb}, 0.22);
  transition: ${theme.transitions.default};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: ${theme.colors.primaryLight};
    box-shadow: 0 0 0 4px rgba(${theme.colors.primaryRgb}, 0.12);
    flex-shrink: 0;
  }

  &:hover {
    background: rgba(${theme.colors.primaryRgb}, 0.18);
    border-color: rgba(${theme.colors.primaryRgb}, 0.32);
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.glow};
  }
`;

export const Hamburger = styled.button<{ $open: boolean }>`
  display: none;
  justify-self: end;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 42px;
  height: 42px;
  background: rgba(8, 12, 18, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 14px;
  z-index: 1001;
  transition: ${theme.transitions.default};

  span {
    display: block;
    width: 18px;
    height: 2px;
    background: ${theme.colors.text};
    border-radius: 2px;
    transition: transform 0.25s ease, opacity 0.25s ease;
    transform-origin: center;
  }

  &:hover {
    border-color: rgba(${theme.colors.primaryRgb}, 0.24);
  }

  ${({ $open }) =>
    $open &&
    css`
      border-color: rgba(${theme.colors.primaryRgb}, 0.3);

      span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
      span:nth-child(2) { opacity: 0; transform: scaleX(0); }
      span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }
    `}

  @media (max-width: ${theme.breakpoints.md}) {
    display: flex;
  }
`;

export const MobileDrawer = styled.div<{ $open: boolean }>`
  display: none;

  @media (max-width: ${theme.breakpoints.md}) {
    display: grid;
    grid-template-rows: auto 1fr auto;
    gap: 1rem;
    position: fixed;
    inset: 0;
    padding: 104px 1rem 1.1rem;
    background:
      radial-gradient(circle at top left, rgba(${theme.colors.primaryRgb}, 0.16), transparent 30%),
      linear-gradient(180deg, rgba(11, 17, 23, 0.98), rgba(6, 10, 16, 0.98));
    box-shadow: 0 24px 64px rgba(2, 6, 23, 0.42);
    z-index: 999;
    overflow-y: auto;
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    pointer-events: ${({ $open }) => ($open ? 'all' : 'none')};
    transform: translateY(${({ $open }) => ($open ? '0' : '-10px')});
    transition: transform 0.28s ease, opacity 0.28s ease;
  }
`;

export const MobileHeader = styled.div`
  align-self: start;
  max-width: 320px;
`;

export const MobileLabel = styled.span`
  display: inline-flex;
  margin-bottom: 0.6rem;
  color: ${theme.colors.primaryLight};
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

export const MobileNote = styled.p`
  color: ${theme.colors.text};
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: clamp(1.7rem, 7vw, 2.35rem);
  font-weight: 700;
  line-height: 0.98;
  letter-spacing: -0.05em;
`;

export const MobileNavGroup = styled.div`
  display: grid;
  gap: 0.75rem;
  align-content: start;
`;

export const MobileLink = styled(Link)<{ $active?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 0.95rem;
  min-height: 88px;
  padding: 1.15rem 1.1rem;
  border-radius: 24px;
  border: 1px solid ${({ $active }) => ($active ? `rgba(${theme.colors.primaryRgb}, 0.2)` : 'rgba(148, 163, 184, 0.08)')};
  background: ${({ $active }) => ($active ? `rgba(${theme.colors.primaryRgb}, 0.12)` : 'rgba(15, 23, 34, 0.74)')};
  transition: ${theme.transitions.default};

  small {
    min-width: 32px;
    align-self: start;
    font-family: 'Space Grotesk', 'Manrope', sans-serif;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${({ $active }) => ($active ? theme.colors.primaryLight : theme.colors.textMuted)};
  }

  span {
    font-family: 'Space Grotesk', 'Manrope', sans-serif;
    font-size: clamp(1.4rem, 6vw, 1.95rem);
    font-weight: 600;
    line-height: 0.98;
    color: ${({ $active }) => ($active ? theme.colors.text : theme.colors.textSecondary)};
  }

  &:hover {
    transform: translateX(4px);
    border-color: rgba(${theme.colors.primaryRgb}, 0.2);
  }
`;

export const MobileActions = styled.div`
  display: grid;
  gap: 0.9rem;
  align-self: end;
`;

export const MobileCta = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  min-height: 64px;
  padding: 1rem 1rem;
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 0.98rem;
  font-weight: 700;
  color: ${theme.colors.text};
  border-radius: 20px;
  background: rgba(${theme.colors.primaryRgb}, 0.14);
  border: 1px solid rgba(${theme.colors.primaryRgb}, 0.24);
  transition: ${theme.transitions.default};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: ${theme.colors.primaryLight};
    box-shadow: 0 0 0 4px rgba(${theme.colors.primaryRgb}, 0.12);
  }

  &:hover {
    background: rgba(${theme.colors.primaryRgb}, 0.2);
  }
`;

export const Overlay = styled.div<{ $open: boolean }>`
  display: none;

  @media (max-width: ${theme.breakpoints.md}) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(2, 6, 23, 0.72);
    z-index: 998;
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    pointer-events: ${({ $open }) => ($open ? 'all' : 'none')};
    transition: opacity 0.25s ease;
  }
`;

export const LangToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.15rem;
  margin-left: 0;
  padding: 0.2rem;
  border-radius: 999px;
  background: rgba(8, 12, 18, 0.82);
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

export const LangBtn = styled.button<{ $active: boolean }>`
  padding: 0.38rem 0.65rem;
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? theme.colors.gradient : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : theme.colors.textMuted)};
  transition: ${theme.transitions.default};

  &:hover {
    color: ${theme.colors.text};
  }
`;

